import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Table, Input, Select, DatePicker, Button, Space, message, Modal, Upload, Tooltip, Spin, Pagination } from 'antd';
import { SearchOutlined, InboxOutlined, UserAddOutlined, DeleteOutlined, UploadOutlined, FileTextOutlined } from '@ant-design/icons';
import { Search, Filter, ChevronDown, Calendar, Activity, FileText, Download, Upload as UploadIcon } from 'lucide-react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import cookie from 'js-cookie';
import { getStudiesForClinical, getStudyDetail, getReportForStudy, getAllReportsForStudy, getBulkReportStatus, getStudyAssignments, getPrimaryModality, getUserById, getClinicalIndexHealth, getPatientHistory } from './StudiesService';
import PatientHistoryPanel from './PatientHistoryPanel';
import { priorVisitsFor, regionsOf, reportStudyOf, firstImpressionLine } from './patientPriors';
import { getMyTeleradiologyRequests } from '../teleradiology/TeleradiologyService';
import { actionShowUploadModal, actionGetUsers, actionRefreshToken } from '../system/systemAction';
import { openStudyInWorkstation } from '../../utils/workstation';
import { actionCreateSession } from '../studyList/StudyListAction';
import { AssignStudyDialog } from '../../components/AssignStudyDialog/AssignStudyDialog';
import { DeleteStudyDialog } from '../../components/DeleteStudyDialog/DeleteStudyDialog';
import { UnassignStudyDialog } from '../../components/UnassignStudyDialog/UnassignStudyDialog';
import { DownloadStudyDialog } from '../../components/DownloadStudyDialog/DownloadStudyDialog';
import { ShareStudyDialog } from '../../components/ShareStudyDialog/ShareStudyDialog';
import { UploadStudyDialog } from '../../components/UploadStudyDialog/UploadStudyDialog';
import { BulkDownloadDialog } from '../../components/BulkDownloadDialog/BulkDownloadDialog';
import { BulkAssignDialog } from '../../components/BulkAssignDialog/BulkAssignDialog';
import { BulkDeleteDialog } from '../../components/BulkDeleteDialog/BulkDeleteDialog';
import StudyTableActions from '../../components/StudyTableActions/StudyTableActions';
import { SESSION_TYPE, routes, STUDIES_FETCH_LIMIT, STUDIES_REFETCH_DEBOUNCE_MS, TOKEN, REFRESH_TOKEN } from '../../utils/constants/config';
import './Studies.scss';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Studies = (props) => {
  const {
    uploadInfoModal = {},
    history,
    currentProject = {},
    users = {},
    profile = {},
  } = props;
  const { formatMessage: t } = useIntl();
  // loading=true on mount so the table shows a spinner during the first
  // fetch instead of flashing the empty-state "No data" illustration.
  const [loading, setLoading] = useState(true);
  const [studiesData, setStudiesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  // Server-side mode (Phase 2): when this tenant is on the ES clinical index,
  // the table paginates/filters/sorts server-side instead of loading the whole
  // corpus and filtering in the browser. Decided once on mount via the health
  // probe; false → the legacy client-side path runs unchanged.
  const [serverMode, setServerMode] = useState(false);
  const [healthChecked, setHealthChecked] = useState(false);
  const [serverTotal, setServerTotal] = useState(0);
  // Default sort: newest study date first (matches user expectation, e.g.
  // 2026-06-19, 18, 17 …). The backend adds last_update as a secondary key.
  const [sortParam, setSortParam] = useState('-study_date');
  const [searchParams, setSearchParams] = useState({
    patientName: '',
    patientID: '',
    accessionNumber: '',
    modality: 'All',
    status: 'All',
    reportStatus: 'All',
    dateRange: null,
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowItems, setSelectedRowItems] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedStudyForAction, setSelectedStudyForAction] = useState(null);
  const [showBulkDownloadModal, setShowBulkDownloadModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reportStatusCache, setReportStatusCache] = useState({});
  const [reportStatusLoading, setReportStatusLoading] = useState({});
  // Attested signer per study (from bulk-status), so a completed-but-unassigned
  // study can show WHO signed it in the Assigned To column. { uid: {name, sub} }
  const [reportSignerCache, setReportSignerCache] = useState({});
  const [assignmentCache, setAssignmentCache] = useState({});
  // Teleradiology status for the hospital's submitted studies, keyed by study UID.
  // Populated once per load from /teleradiology/my-requests (returns a ready status_map).
  const [teleStatusMap, setTeleStatusMap] = useState({});
  const [showReportPreviewModal, setShowReportPreviewModal] = useState(false);
  const [reportPreviewData, setReportPreviewData] = useState(null);
  const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
  const [reportPreviewStudy, setReportPreviewStudy] = useState(null);
  const [reportPreviewRadiologistName, setReportPreviewRadiologistName] = useState('');
  // Patient/study table values as EDITED by the radiologist in the report editor
  // (persisted as an afya-patient-meta comment in the report content). The preview
  // must show these, not the original DICOM values from the study record.
  const [reportPreviewMeta, setReportPreviewMeta] = useState(null);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [assignmentLoading, setAssignmentLoading] = useState({});
  // Priors badge: every known study per patient ID (from /patient-history), the
  // row whose history panel is open, and impression lines keyed by study UID
  // (undefined = not asked, null = loading, '' = none).
  const [patientHistory, setPatientHistory] = useState({});
  const [historyPanelRow, setHistoryPanelRow] = useState(null);
  const [priorImpressions, setPriorImpressions] = useState({});
  const historyRequestedRef = useRef(new Set());
  const [tableRenderKey, setTableRenderKey] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const normalizeReportStatus = (status) => {
    const value = (status || 'none').toLowerCase();
    if (value === 'none' || value === 'draft' || value === 'submitted') {
      return value;
    }
    return 'unknown';
  };

  const normalizeStudyUID = (studyUID) => {
    if (!studyUID || typeof studyUID !== 'string') return '';
    if (studyUID.match(/^[a-f0-9-]{36}\./i)) {
      return studyUID.substring(37);
    }
    return studyUID;
  };

  const reportStatusLabels = {
    none: 'Pending',
    draft: 'In Progress',
    submitted: 'Completed',
    unknown: 'Unknown',
  };

  // Label shown in the "Assigned To" column for studies sent to the teleradiology
  // network (overrides any local-radiologist assignee).
  const TELERADIOLOGY_LABEL = 'Teleradiology';

  const getUserDisplayName = (userId) => {
    if (!userId) return '';
    const user = (users?.data || []).find((item) => item.id === userId);
    if (!user) return userId;
    const nameParts = [user.first_name, user.last_name].filter(Boolean);
    const fullName = nameParts.length > 0 ? nameParts.join(' ') : '';
    return fullName || user.username || user.id;
  };

  // Compact display for the DICOM referring/ordering physician so the new
  // "Referring Physician" column stays narrow. Normalizes DICOM PN carets to
  // spaces, collapses whitespace, and truncates long names — the full value is
  // preserved in the cell's title tooltip.
  const abbreviatePhysician = (raw) => {
    if (!raw || raw === 'N/A') return '';
    const clean = String(raw).replace(/\^/g, ' ').replace(/\s+/g, ' ').trim();
    if (!clean) return '';
    return clean.length > 14 ? `${clean.slice(0, 13)}…` : clean;
  };

  const getCurrentUserId = () =>
    profile?.sub || profile?.id || profile?.user_id || '';

  const getUserRoles = () => profile?.realm_roles || [];

  const isAdminUser = () => {
    const roles = getUserRoles();
    const adminRoles = ['PROJECT_OWNER', 'MANAGER', 'ADMIN', 'Administrator'];
    return roles.some((role) => adminRoles.includes(role));
  };

  const isRadiologistUser = () => {
    const roles = getUserRoles();
    const radiologistRoles = ['ANNOTATOR', 'REVIEWER', 'LABELER', 'Labeler'];
    return roles.some((role) => radiologistRoles.includes(role));
  };

  // Referring physicians: read-only access to COMPLETED studies only. Every
  // physician-specific branch below is gated on this, so all other roles see
  // exactly the same behavior as before.
  const isPhysicianUser = () => getUserRoles().includes('REFERRING_PHYSICIAN');

  // Central data-fetch hook: fires on mount (refreshKey=0) and whenever
  // refreshKey is incremented (e.g. after upload). Because the useEffect runs
  // AFTER React re-renders, fetchStudies always has fresh closures — no stale
  // state issues that plagued the old direct-call approach.
  // Decide server-side vs client-side mode ONCE before the first fetch, so a
  // large-corpus tenant never loads the whole list even on initial load. The
  // probe is lightweight (no Orthanc scan) and fails closed to client mode.
  useEffect(() => {
    getClinicalIndexHealth().then((h) => {
      setServerMode(!!h.enabled);
      setHealthChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!healthChecked) return; // wait until we know which mode to use
    fetchStudies(); // branches internally on serverMode
    if (refreshKey === 0) {
      props.actionGetUsers();
    }
    // eslint-disable-next-line
  }, [healthChecked, refreshKey]);

  // re-fetch when the tab becomes visible again (e.g. user opens viewer tab then
  // comes back). We use ONLY visibilitychange — `window.focus` fires on every
  // window-manager focus event including alt-tab away and back, which doubled
  // the refetch rate and amplified token-refresh races. visibilitychange is the
  // canonical "the user is looking at this tab again" signal.
  //
  // A debounce guard skips refetches that happen within STUDIES_REFETCH_DEBOUNCE_MS
  // of the last fetch, so rapid tab-switching doesn't hammer Orthanc.
  const fetchStudiesRef = useRef(null);
  const lastFetchAtRef = useRef(0);
  // Guards the "corpus outgrew client-side filtering" warning so it fires once
  // per mount rather than on every (re)fetch.
  const truncationWarnedRef = useRef(false);
  useEffect(() => { fetchStudiesRef.current = fetchStudies; });

  // Refresh the list when the global Upload Study dialog (rendered by App.js
  // and triggered from the page header) reports a successful upload. This
  // mirrors the refreshKey bump done by the page-local UploadStudyDialog below.
  useEffect(() => {
    const onStudyUploaded = () => setRefreshKey((prev) => prev + 1);
    window.addEventListener('study-uploaded', onStudyUploaded);
    return () => window.removeEventListener('study-uploaded', onStudyUploaded);
  }, []);

  useEffect(() => {
    const handleReportStatusUpdate = (event) => {
      if (event.key !== 'report-status-update' || !event.newValue) {
        return;
      }

      try {
        console.log('[ADMIN DASHBOARD] Received report status update event:', event.newValue);
        const payload = JSON.parse(event.newValue);
        const rawStudyInstanceUID = payload?.rawStudyInstanceUID || payload?.studyInstanceUID;
        const studyInstanceUID = normalizeStudyUID(payload?.studyInstanceUID || rawStudyInstanceUID);
        const status = normalizeReportStatus(payload?.status);
        console.log('[ADMIN DASHBOARD] Parsed UIDs:', { studyInstanceUID, rawStudyInstanceUID, status });
        if (!studyInstanceUID && !rawStudyInstanceUID) return;

        setReportStatusCache((prev) => {
          const updated = { ...prev };
          let changed = false;
          [studyInstanceUID, rawStudyInstanceUID].filter(Boolean).forEach((uid) => {
            const existingRank = STATUS_RANK[prev[uid]] ?? -1;
            const newRank = STATUS_RANK[status] ?? 0;
            // Only update if the incoming status is strictly better than what
            // we already have — prevents the viewer writing 'none' to localStorage
            // from downgrading a cached 'draft' or 'submitted' status.
            if (newRank > existingRank) {
              updated[uid] = status;
              changed = true;
            }
          });
          if (!changed) return prev;
          console.log('[ADMIN DASHBOARD] Updated cache:', updated);
          return updated;
        });
      } catch (error) {
        console.error('[ADMIN DASHBOARD] Failed to parse report status update:', error);
      }
    };

    window.addEventListener('storage', handleReportStatusUpdate);
    return () => window.removeEventListener('storage', handleReportStatusUpdate);
  }, []);

  // Status rank — used to prevent the viewer writing 'none' to localStorage
  // from downgrading a cached 'draft' or 'submitted' status.
  const STATUS_RANK = { none: 0, unknown: 0, draft: 1, submitted: 2 };

  // Handle page visibility — single consolidated handler that:
  //   (a) re-fetches the studies list (silent, no spinner) so newly uploaded
  //       or assigned studies appear without manual refresh, and
  //   (b) re-fetches report statuses so completed reports update the badge.
  // Debounced via lastFetchAtRef to avoid flooding the API on rapid tab switches.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastFetchAtRef.current < STUDIES_REFETCH_DEBOUNCE_MS) return;
      lastFetchAtRef.current = now;
      if (fetchStudiesRef.current) {
        fetchStudiesRef.current({ silent: true });
      } else if (studiesData.length > 0) {
        fetchReportStatusesForStudies(studiesData);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [studiesData]);

  useEffect(() => {
    const assignmentKeys = Object.keys(assignmentCache || {});
    const teleKeys = Object.keys(teleStatusMap || {});
    if (assignmentKeys.length === 0 && teleKeys.length === 0) return;

    setStudiesData((prev) => {
      let hasChange = false;
      const next = prev.map((item) => {
        // 1. Teleradiology takes precedence — a study sent to the teleradiology
        //    network is no longer a local-radiologist assignment. Direct O(1)
        //    lookup from the hospital status_map (also try the normalized UID in
        //    case the row carries a project-prefixed UID).
        const tele = teleStatusMap[item.studyInstanceUID]
          || teleStatusMap[normalizeStudyUID(item.studyInstanceUID)];
        if (tele) {
          if (item.status === 'TELERADIOLOGY' && item.referringPhysician === TELERADIOLOGY_LABEL) return item;
          hasChange = true;
          return { ...item, status: 'TELERADIOLOGY', referringPhysician: TELERADIOLOGY_LABEL };
        }
        // 2. Otherwise fall back to the local assignment (unchanged behaviour).
        const assigneeId = assignmentCache[item.studyInstanceUID];
        if (!assigneeId) return item;
        const assignedName = getUserDisplayName(assigneeId) || 'Assigned';
        // Only allocate a new object when something actually changed — returning
        // the old reference prevents React from re-rendering unchanged rows.
        if (item.status === 'ASSIGNED' && item.referringPhysician === assignedName) return item;
        hasChange = true;
        return { ...item, status: 'ASSIGNED', referringPhysician: assignedName };
      });
      // If nothing changed, return the original array reference so React skips
      // the re-render entirely — this stops the column from continuously flickering.
      return hasChange ? next : prev;
    });
  }, [assignmentCache, teleStatusMap, users]);

  const fetchStudies = async (params = {}, _retry = 0) => {
    try {
      if (!params.silent) setLoading(true);

      // SERVER-SIDE MODE: ask the backend for just one page, with all filters,
      // sort and pagination applied server-side. CLIENT MODE (unchanged): pull
      // the whole pool and filter/sort/paginate in the browser.
      let queryParams;
      if (serverMode) {
        const sp = params.searchParams || searchParams;
        const pg = params.pagination || pagination;
        const sort = params.sort || sortParam;
        queryParams = {
          _offset: Math.max(0, (pg.current - 1) * pg.pageSize),
          _limit: pg.pageSize,
          sort,
        };
        // The search box sets patientName/patientID/accessionNumber all to the
        // same term; map that to the backend's OR-across-fields `search` param.
        const term = params.search !== undefined
          ? params.search
          : (sp.patientName || sp.patientID || sp.accessionNumber || '');
        if (term) queryParams.search = term;
        if (sp.modality && sp.modality !== 'All') queryParams.modality = sp.modality;
        if (sp.reportStatus && sp.reportStatus !== 'All') queryParams.report_status = sp.reportStatus;
        if (sp.status && sp.status !== 'All') queryParams.status = sp.status;
        if (sp.dateRange && sp.dateRange[0] && sp.dateRange[1]) {
          queryParams.study_date_from = sp.dateRange[0].format('YYYYMMDD');
          queryParams.study_date_to = sp.dateRange[1].format('YYYYMMDD');
        }
      } else {
        queryParams = {
          _offset: params._offset || 0,
          // Client-side filter pool size. Tunable per-deployment via
          // REACT_APP_STUDIES_FETCH_LIMIT.
          _limit: params._limit || STUDIES_FETCH_LIMIT,
        };
      }

      const { data } = await getStudiesForClinical(queryParams);

      setTotalCount(typeof data?.total === 'number' ? data.total : 0);
      if (serverMode) {
        // Server returns the exact total for the current filter set.
        setServerTotal(typeof data?.total === 'number' ? data.total : 0);
      } else if (data?.hasMore && !truncationWarnedRef.current) {
        // Client-mode only: hasMore means the corpus outgrew the single fetch.
        truncationWarnedRef.current = true;
        message.warning(
          `Showing the first ${data.data?.length ?? 0} of ${data.total} studies. ` +
          `This tenant has outgrown client-side filtering — switch All Studies to server-side search.`,
          8
        );
      }

      // Parse Orthanc study format
      const formattedData = (data?.data || []).map((study, index) => {
        // Merge MainDicomTags with RequestedTags (extra fields requested via
        // ?requested-tags= — ModalitiesInStudy, NumberOfStudyRelated*, BodyPartExamined).
        // SeriesData is kept as a fallback for deployments that still return it.
        const mainTags = { ...(study.MainDicomTags || {}), ...(study.RequestedTags || {}) };
        const patientTags = study.PatientMainDicomTags || {};
        const seriesTags = study.SeriesData?.MainDicomTags || {};
        
        // Get modality and body part from series (SR/derived modalities are excluded)
        const modality = getPrimaryModality(seriesTags.Modality, mainTags.ModalitiesInStudy);
        const bodyPart = seriesTags.BodyPartExamined || mainTags.BodyPartExamined || 'N/A';
        const seriesDescription = seriesTags.SeriesDescription || mainTags.StudyDescription || '';

        // Preserve raw StudyInstanceUID and derive clean UID for display/cache keys
        const rawStudyUID = mainTags.StudyInstanceUID || '';
        let studyUID = rawStudyUID;
        if (studyUID.includes('.') && studyUID.match(/^[a-f0-9-]{36}\./i)) {
          studyUID = studyUID.substring(37);
        }

        // Series and image counts — prefer DICOM tags, fall back to Orthanc statistics
        const seriesCount = parseInt(mainTags.NumberOfStudyRelatedSeries, 10)
          || parseInt(study.CountSeries, 10)
          || (study.Series ? study.Series.length : 0)
          || 0;
        const imagesCount = parseInt(mainTags.NumberOfStudyRelatedInstances, 10)
          || parseInt(study.CountInstances, 10)
          || 0;

        return {
          key: study.ID || index,
          id: study.ID,
          patientName: patientTags.PatientName || 'N/A',
          status: 'NEW', // Default status for clinical workflow
          patientID: patientTags.PatientID || 'N/A',
          birthDateRaw: patientTags.PatientBirthDate || '', // Priors identity check
          accessionNumber: mainTags.AccessionNumber || 'N/A',
          orderingPhysician: mainTags.ReferringPhysicianName || 'N/A',
          sex: patientTags.PatientSex || 'U',
          age: patientTags.PatientBirthDate && patientTags.PatientBirthDate !== '' 
            ? calculateAge(patientTags.PatientBirthDate) 
            : 'N/A',
          bodyPart: bodyPart,
          seriesCount,
          imagesCount,
          modality: modality,
          studyDate: mainTags.StudyDate && mainTags.StudyTime
            ? moment(mainTags.StudyDate + mainTags.StudyTime.substring(0, 6), 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm')
            : mainTags.StudyDate
            ? moment(mainTags.StudyDate, 'YYYYMMDD').format('YYYY-MM-DD')
            : 'N/A',
          studyDateRaw: mainTags.StudyDate || '', // For filtering
          lastUpdate: study.LastUpdate || '', // Orthanc ingestion timestamp (YYYYMMDDTHHmmss)
          // "Assigned To" slot — MUST stay empty until a real assignment or
          // teleradiology overlay fills it. Previously this was seeded from the
          // DICOM ReferringPhysicianName, which made every unassigned study show
          // the ordering physician in the Assigned To column. The referring/
          // ordering physician now has its own column (see `orderingPhysician`).
          referringPhysician: '',
          studyInstanceUID: studyUID,
          rawStudyInstanceUID: rawStudyUID,
          studyDescription: seriesDescription,
        };
      });

      let filteredData = formattedData;

      // Apply client-side filtering — skipped in server mode, where the backend
      // already returned the exact filtered page.
      if (!serverMode) {
      // Filter by patient name
      if (searchParams.patientName && searchParams.patientName.trim() !== '') {
        const searchName = searchParams.patientName.toLowerCase().trim();
        filteredData = filteredData.filter(study => 
          study.patientName.toLowerCase().includes(searchName)
        );
      }

      // Filter by patient ID
      if (searchParams.patientID && searchParams.patientID.trim() !== '') {
        const searchID = searchParams.patientID.toLowerCase().trim();
        filteredData = filteredData.filter(study => 
          study.patientID.toLowerCase().includes(searchID)
        );
      }

      // Filter by accession number
      if (searchParams.accessionNumber && searchParams.accessionNumber.trim() !== '') {
        const searchAccession = searchParams.accessionNumber.toLowerCase().trim();
        filteredData = filteredData.filter(study => 
          study.accessionNumber.toLowerCase().includes(searchAccession)
        );
      }

      // Filter by modality
      if (searchParams.modality && searchParams.modality !== 'All') {
        filteredData = filteredData.filter(study => 
          study.modality === searchParams.modality
        );
      }

      // Filter by date range
      if (searchParams.dateRange && searchParams.dateRange.length === 2) {
        const startDate = searchParams.dateRange[0].format('YYYYMMDD');
        const endDate = searchParams.dateRange[1].format('YYYYMMDD');
        filteredData = filteredData.filter(study => {
          const studyDate = study.studyDateRaw || '';
          return studyDate >= startDate && studyDate <= endDate;
        });
      }
      } // end client-side filtering (!serverMode)

      const roles = getUserRoles();
      const currentUserId = getCurrentUserId();
      let assignments = {};

      if (filteredData.length > 0) {
        const studyUIDs = Array.from(
          new Set(
            filteredData
              .map((study) => study.studyInstanceUID)
              .filter((uid) => uid && uid.trim() !== '')
          )
        );

        if (studyUIDs.length > 0) {
          // Fire-and-forget: don't await — assignments update the cache in the background
          // so setStudiesData runs immediately instead of waiting up to 30s for ES.
          getStudyAssignments(studyUIDs).then(({ data }) => {
            const fetched = data?.data || {};
            // Merge without clearing first — clearing then re-adding fires the
            // downstream useEffect twice per refresh, causing the Assigned To
            // column to visibly flicker on every silent re-fetch.
            setAssignmentCache((prev) => ({ ...prev, ...fetched }));
          }).catch((error) => {
            console.error('Failed to fetch study assignments:', error);
          });

          // Teleradiology status for the whole hospital in ONE call — the backend
          // returns a ready status_map (studyUID -> {status,urgency}), so the merge
          // effect above is O(1) per row. Fire-and-forget, mirroring assignments.
          // Replace (not merge): my-requests is the complete authoritative picture,
          // so a study removed/recalled from teleradiology correctly clears here.
          getMyTeleradiologyRequests().then((res) => {
            setTeleStatusMap(res?.data?.status_map || {});
          }).catch((error) => {
            console.error('Failed to fetch teleradiology status:', error);
          });
        }
      }

      // Sort newest-ingestion-first (client mode only; in server mode the
      // backend already returned the page in the requested sort order).
      if (!serverMode) {
        filteredData.sort((a, b) => {
          const da = a.lastUpdate || a.studyDateRaw || '';
          const db = b.lastUpdate || b.studyDateRaw || '';
          if (db > da) return 1;
          if (db < da) return -1;
          return 0;
        });
      }

      // Store full filtered dataset — Ant Design Table handles pagination natively.
      // This avoids stale-closure pagination bugs from manual slicing.
      console.log('[Studies] setStudiesData called with', filteredData.length, 'studies');
      setStudiesData(filteredData);
      fetchReportStatusesForStudies(filteredData);
      fetchPatientHistoryForStudies(filteredData);
      lastFetchAtRef.current = Date.now();
      // Always clear loading after a successful fetch — a silent refetch that
      // happens to be the very first fetch (race between mount-effect and
      // visibilitychange) must still hide the initial spinner.
      setLoading(false);
    } catch (error) {
      console.error('Error fetching studies (attempt ' + (_retry + 1) + '):', error);
      if (_retry < 2) {
        // Auto-retry up to 2 times with 3 s delay.
        // Handles OIDC token-refresh races and brief API/Orthanc startup delays.
        setTimeout(() => fetchStudies(params, _retry + 1), 3000);
        return;
      }
      if (!params.silent) message.error('Failed to fetch studies');
      // Always clear loading on terminal failure so the table shows the
      // empty-state instead of a perpetual spinner.
      setLoading(false);
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return 'N/A';
    try {
      const birth = moment(birthDate, 'YYYYMMDD');
      const age = moment().diff(birth, 'years');
      return age > 0 ? `${age}Y` : 'N/A';
    } catch {
      return 'N/A';
    }
  };

  const fetchReportStatusesForStudies = async (studies) => {
    const studyUIDPairs = (studies || [])
      .map((study) => ({
        cacheUID: study.studyInstanceUID,
        queryUID: study.rawStudyInstanceUID || study.studyInstanceUID,
      }))
      .filter((item) => item.cacheUID && item.cacheUID.trim() !== '');

    if (studyUIDPairs.length === 0) return;

    const uniqueByCacheUID = Array.from(new Map(
      studyUIDPairs.map((item) => [item.cacheUID, item])
    ).values());

    const pendingPairs = uniqueByCacheUID.filter(
      ({ cacheUID }) => reportStatusCache[cacheUID] === undefined && !reportStatusLoading[cacheUID]
    );

    if (pendingPairs.length === 0) return;

    // Mark as loading
    setReportStatusLoading((prev) => {
      const next = { ...prev };
      pendingPairs.forEach(({ cacheUID }) => {
        next[cacheUID] = true;
      });
      return next;
    });

    // Single bulk call — one POST replaces N per-study /history round-trips
    // (this page used to fire ~1 request per study, up to ~580, only ~6 at a
    // time in the browser). The backend returns { <queryUID>: { status } } with
    // status already computed as 'submitted' | 'draft' | 'none'.
    let statusResults;
    try {
      const queryUIDs = pendingPairs.map(({ queryUID }) => queryUID);
      const response = await getBulkReportStatus(queryUIDs);
      const bulk = response.data?.data || {};
      statusResults = pendingPairs.map(({ cacheUID, queryUID }) => {
        const entry = bulk[queryUID] || bulk[cacheUID] || {};
        return {
          cacheUID, queryUID,
          status: entry.status || 'none',
          signerName: entry.signer_name || '',
          signerSub: entry.signer_sub || '',
          draftAuthorSub: entry.draft_author_sub || '',
        };
      });
    } catch (error) {
      console.error('[Report Status] bulk fetch failed:', error?.message);
      statusResults = pendingPairs.map(({ cacheUID, queryUID }) => ({ cacheUID, queryUID, status: 'none', signerName: '', signerSub: '', draftAuthorSub: '' }));
    }

    // Update cache with fetched statuses
    setReportStatusCache((prev) => {
      const next = { ...prev };
      statusResults.forEach(({ cacheUID, queryUID, status }) => {
        next[cacheUID] = status;
        if (queryUID) {
          next[queryUID] = status;
        }
      });
      return next;
    });

    // Cache the report's people alongside its status, for the Assigned To
    // fallback: `sub` is who SIGNED a submitted report, `draftSub` is who is
    // currently WRITING an unsubmitted one.
    setReportSignerCache((prev) => {
      const next = { ...prev };
      statusResults.forEach(({ cacheUID, queryUID, signerName, signerSub, draftAuthorSub }) => {
        const v = { name: signerName, sub: signerSub, draftSub: draftAuthorSub };
        next[cacheUID] = v;
        if (queryUID) next[queryUID] = v;
      });
      return next;
    });

    // Clear loading state
    setReportStatusLoading((prev) => {
      const next = { ...prev };
      pendingPairs.forEach(({ cacheUID }) => {
        delete next[cacheUID];
      });
      return next;
    });
  };

  // Patient history for the priors badge, fetched once per row (a silent
  // refetch only asks about rows it has not seen). Each answer replaces that
  // patient's whole list, so a newly arrived study of a known patient still
  // shows up in the older rows' history. Fire-and-forget: without it the table
  // simply has no badges.
  const HISTORY_CHUNK = 2000;
  const fetchPatientHistoryForStudies = async (studies) => {
    const uids = Array.from(new Set((studies || [])
      .map((study) => study.rawStudyInstanceUID || study.studyInstanceUID)
      .filter((uid) => uid && !historyRequestedRef.current.has(uid))));
    if (uids.length === 0) return;
    uids.forEach((uid) => historyRequestedRef.current.add(uid));
    for (let start = 0; start < uids.length; start += HISTORY_CHUNK) {
      const chunk = uids.slice(start, start + HISTORY_CHUNK);
      try {
        const response = await getPatientHistory(chunk);
        const byPatient = {};
        (response.data?.data?.studies || []).forEach((s) => {
          const pid = (s.patient_id || '').trim();
          if (!pid) return;
          (byPatient[pid] = byPatient[pid] || []).push(s);
        });
        if (Object.keys(byPatient).length > 0) {
          setPatientHistory((prev) => ({ ...prev, ...byPatient }));
        }
      } catch (error) {
        // Let the next refetch try these rows again.
        chunk.forEach((uid) => historyRequestedRef.current.delete(uid));
        console.error('[Priors] patient history fetch failed:', error?.message);
      }
    }
  };

  const priorsByRow = useMemo(() => {
    const out = {};
    studiesData.forEach((row) => {
      const pid = (row.patientID || '').trim();
      const history = pid && pid !== 'N/A' ? patientHistory[pid] : null;
      if (history) out[row.key] = priorVisitsFor(row, history);
    });
    return out;
  }, [studiesData, patientHistory]);

  const historyPanel = historyPanelRow ? priorsByRow[historyPanelRow.key] : null;

  // On opening a row's history, load the impression line of each visit's
  // signed report (one report fetch per visit, cached across openings).
  useEffect(() => {
    if (!historyPanelRow || !historyPanel) return;
    const rowRegions = regionsOf(historyPanelRow.bodyPart, historyPanelRow.studyDescription);
    const wanted = historyPanel.visits
      .map((visit) => reportStudyOf(visit, rowRegions))
      .filter((s) => s && priorImpressions[s.study_instance_uid] === undefined)
      .map((s) => s.study_instance_uid);
    if (wanted.length === 0) return;
    setPriorImpressions((prev) => {
      const next = { ...prev };
      wanted.forEach((uid) => { next[uid] = null; });
      return next;
    });
    wanted.forEach((uid) => {
      getAllReportsForStudy(uid).then((response) => {
        const signed = (response.data?.reports || [])
          .filter((r) => r.status === 'submitted')
          .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
        const line = signed[0] ? firstImpressionLine(signed[0].content) : '';
        setPriorImpressions((prev) => ({ ...prev, [uid]: line }));
      }).catch(() => {
        setPriorImpressions((prev) => ({ ...prev, [uid]: '' }));
      });
    });
  }, [historyPanelRow, historyPanel]);

  const closeHistoryPanel = useCallback(() => setHistoryPanelRow(null), []);

  // The report preview reads row-shaped fields; build them from the prior.
  const handleOpenPriorReport = (prior) => {
    const row = historyPanelRow || {};
    const born = moment(prior.patient_birth_date || '', 'YYYYMMDD', true);
    const taken = moment(prior.study_date || '', 'YYYYMMDD', true);
    handleOpenReportPreview({
      studyInstanceUID: prior.study_instance_uid,
      id: prior.orthanc_id,
      patientName: prior.patient_name || row.patientName,
      patientID: prior.patient_id || row.patientID,
      sex: row.sex,
      age: born.isValid() && taken.isValid() ? `${taken.diff(born, 'years')}Y` : '',
      modality: prior.modality,
      studyDate: taken.isValid() ? taken.format('YYYY-MM-DD') : 'N/A',
      orderingPhysician: 'N/A',
    });
  };

  const renderPriorsPill = (record) => {
    const priors = priorsByRow[record.key];
    if (!priors || priors.visits.length === 0) return null;
    const n = priors.visits.length;
    return (
      <button
        type="button"
        className="priors-pill"
        aria-label={`Show ${n} prior visits`}
        onClick={(e) => { e.stopPropagation(); setHistoryPanelRow(record); }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="M12 7v5l3 2" />
        </svg>
        {n} prior {n === 1 ? 'visit' : 'visits'}
        {priors.relevant > 0 && (
          <span className="priors-pill-relevant"><span className="priors-pill-dot" />{priors.relevant} relevant</span>
        )}
      </button>
    );
  };

  const fetchAssignmentsForStudies = async (studies) => {
    const studyUIDs = (studies || [])
      .map((study) => study.studyInstanceUID)
      .filter((uid) => uid && uid.trim() !== '');

    if (studyUIDs.length === 0) return;

    const uniqueUIDs = Array.from(new Set(studyUIDs));
    const pendingUIDs = uniqueUIDs.filter(
      (uid) => !assignmentCache[uid] && !assignmentLoading[uid]
    );

    if (pendingUIDs.length === 0) return;

    setAssignmentLoading((prev) => {
      const next = { ...prev };
      pendingUIDs.forEach((uid) => {
        next[uid] = true;
      });
      return next;
    });

    try {
      const { data } = await getStudyAssignments(pendingUIDs);
      const assignments = data?.data || {};

      setAssignmentCache((prev) => ({
        ...prev,
        ...assignments,
      }));
    } catch (error) {
      console.error('Failed to fetch study assignments:', error);
    } finally {
      setAssignmentLoading((prev) => {
        const next = { ...prev };
        pendingUIDs.forEach((uid) => {
          delete next[uid];
        });
        return next;
      });
    }
  };

  const renderReportStatusCell = (record) => {
    const studyInstanceUID = record.studyInstanceUID;
    
    // If no study UID, we can't fetch status
    if (!studyInstanceUID) {
      return <span className="report-status report-status-unknown">-</span>;
    }

    // Show loading spinner while fetching
    if (reportStatusLoading[studyInstanceUID]) {
      return (
        <span className="report-status report-status-loading">
          <Spin size="small" />
          <span style={{ marginLeft: '4px' }}>Loading</span>
        </span>
      );
    }

    // Get cached status - should be populated by fetchReportStatusesForStudies.
    // Reports the FACT of whether a report exists, independent of assignment:
    // a draft is shown as such even on an unassigned study, because the person
    // reading this column may well be the one writing that draft.
    const status = reportStatusCache[studyInstanceUID];
    
    // If status is in cache, display it (could be 'none', 'draft', 'submitted', or 'unknown')
    if (status) {
      const statusLabel = reportStatusLabels[status] || status;
      return (
        <span className={`report-status report-status-${status}`}>
          <span className="report-status-dot" />
          <span>{statusLabel}</span>
          {status === 'submitted' && (
            <Tooltip title="View Report">
              <FileTextOutlined
                className="report-view-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenReportPreview(record);
                }}
              />
            </Tooltip>
          )}
        </span>
      );
    }

    // If status not found in cache and not loading, show unknown
    // This shouldn't happen if fetch is working, but handle gracefully
    return <span className="report-status report-status-unknown">-</span>;
  };

  const handleOpenReportPreview = async (record) => {
    const studyInstanceUID = record.studyInstanceUID;
    if (!studyInstanceUID) return;

    setReportPreviewStudy(record);
    setReportPreviewData(null);
    setReportPreviewRadiologistName('');
    setReportPreviewMeta(null);
    setReportPreviewLoading(true);
    setShowReportPreviewModal(true);
    setModalPosition({ x: 0, y: 0 });

    try {
      const response = await getAllReportsForStudy(studyInstanceUID);
      const reports = (response.data?.reports || []).sort((a, b) => {
        // Prefer submitted entries, then sort by timestamp descending
        if (a.status === 'submitted' && b.status !== 'submitted') return -1;
        if (b.status === 'submitted' && a.status !== 'submitted') return 1;
        return (b.timestamp || '').localeCompare(a.timestamp || '');
      });
      const best = reports[0];
      if (!best) {
        setReportPreviewData(null);
      } else {
        setReportPreviewData({
          content: best.content,
          status: best.status,
          submitted_at: best.timestamp,
          user_id: best.user_id,
        });

        // Restore the radiologist's EDITED patient/study table values from the
        // report's afya-patient-meta comment (same source the editor reads back).
        // Without this the preview rebuilds the table from the study's original
        // DICOM values and silently discards the radiologist's corrections.
        try {
          const pm = (best.content || '').match(/<!--afya-patient-meta:(.*?)-->/);
          setReportPreviewMeta(pm ? JSON.parse(pm[1]) : null);
        } catch (e) {
          setReportPreviewMeta(null);
        }

        // Resolve the signing radiologist. The AUTHORITATIVE signer is persisted
        // inside the report content as an <!--afya-signer-meta:{...}--> comment
        // (the same source of truth the Report Editor reads back via
        // extractSignerMeta). On a shared hospital login the report's user_id is
        // the shared/PO account — NOT the radiologist — so resolving user_id shows
        // the wrong name (e.g. "Dr. Rimna Hospital"). Prefer the attested signer
        // meta; only fall back to user_id when a report carries no meta (legacy
        // reports written before signature attribution existed).
        const signerFromMeta = (() => {
          try {
            const m = (best.content || '').match(/<!--afya-signer-meta:(.*?)-->/);
            if (!m) return '';
            const signer = JSON.parse(m[1]);
            return (signer && signer.name) ? signer.name : '';
          } catch (e) {
            return '';
          }
        })();

        if (signerFromMeta) {
          setReportPreviewRadiologistName(signerFromMeta);
        } else if (best.user_id) {
          // Fallback: no attestation meta — resolve the Keycloak user directly.
          try {
            const { data: userResp } = await getUserById(best.user_id);
            const user = userResp?.data;
            if (user) {
              const nameParts = [user.first_name, user.last_name].filter(Boolean);
              const displayName = nameParts.length > 0
                ? nameParts.join(' ')
                : (user.username || '');
              setReportPreviewRadiologistName(displayName);
            }
          } catch (userErr) {
            console.warn('Could not fetch radiologist user info:', userErr);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
      message.error('Failed to load report');
    } finally {
      setReportPreviewLoading(false);
    }
  };

  const handleReportPreviewDownloadPDF = () => {
    if (!reportPreviewData || !reportPreviewData.content) return;
    const study = reportPreviewStudy || {};
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message.error('Pop-up blocked. Please allow pop-ups to download PDF.');
      return;
    }

    // Prefer the radiologist's edited patient/study values (afya-patient-meta),
    // matching the on-screen preview and the report editor; fall back to the study.
    const pm = reportPreviewMeta || {};
    const patientName = pm.patientName || study.patientName || 'N/A';
    const patientId = pm.patientId || study.patientID || 'N/A';
    const sexAge = (pm.patientSex || pm.patientAge)
      ? (pm.patientSex || '') + (pm.patientAge ? ' / ' + pm.patientAge : '')
      : (study.sex || 'N/A') + (study.age ? ' / ' + study.age : '');
    const studyDate = pm.studyDate || study.studyDate || 'N/A';
    const modality = pm.modality || study.modality || 'N/A';
    const referringPhysician = pm.referringPhysician || study.orderingPhysician || 'N/A';

    const name = reportPreviewRadiologistName;
    const drName = name
      ? (/^dr\.?\s/i.test(name) ? name : 'Dr. ' + name)
      : '';
    const submittedDate = reportPreviewData.submitted_at
      ? new Date(reportPreviewData.submitted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
        ' ' + new Date(reportPreviewData.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      : '';
    const signatureHtml = (reportPreviewData.status === 'submitted' && (drName || submittedDate))
      ? '<div style="margin-top:48px;padding-top:14px;border-top:1px solid #d1d5db;">' +
          '<p style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;margin:0 0 4px 0;">Electronically Signed By</p>' +
          (drName ? '<p style="font-size:14px;font-weight:600;margin:0 0 2px 0;">' + drName + '</p>' : '') +
          (submittedDate ? '<p style="font-size:11px;color:#6b7280;margin:0;">' + submittedDate + '</p>' : '') +
        '</div>'
      : '';

    printWindow.document.write(
      '<!DOCTYPE html><html><head><title>Diagnostic Report</title>' +
      '<style>' +
      '@page{size:letter;margin:20mm;}' +
      'body{font-family:"Times New Roman",Georgia,serif;font-size:14px;line-height:1.6;color:#1a1a1a;padding:40px;max-width:612px;margin:0 auto;}' +
      'h1{text-align:center;font-size:18px;font-weight:700;margin:0 0 4px 0;}' +
      'table{width:100%;border-collapse:collapse;border:1px solid #d1d5db;margin-bottom:24px;}' +
      'td{padding:7px 12px;border:1px solid #d1d5db;font-size:12px;width:25%;}' +
      '.lbl{font-weight:600;background:#f9fafb;}' +
      'h3{font-size:13px;font-weight:700;margin:20px 0 6px 0;text-transform:uppercase;letter-spacing:0.6px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;}' +
      'p{margin:0 0 10px 0;}' +
      '@media print{body{padding:20px;}}' +
      '</style></head><body>' +
      '<h1>DIAGNOSTIC REPORT</h1>' +
      '<table><tbody>' +
      '<tr><td class="lbl">Patient</td><td>' + patientName + '</td><td class="lbl">Referring Physician</td><td>' + referringPhysician + '</td></tr>' +
      '<tr><td class="lbl">Patient ID</td><td>' + patientId + '</td><td class="lbl">Date</td><td>' + studyDate + '</td></tr>' +
      '<tr><td class="lbl">Sex / Age</td><td>' + sexAge + '</td><td class="lbl">Modality</td><td>' + modality + '</td></tr>' +
      '</tbody></table>' +
      reportPreviewData.content.replace(/^\s*<h1[^>]*>.*?DIAGNOSTIC\s+REPORT.*?<\/h1>\s*/is, '').replace(/^\s*<table[\s\S]*?<\/table>\s*/i, '') +
      signatureHtml +
      '</body></html>'
    );
    printWindow.document.close();
    printWindow.focus();
    setTimeout(function () { printWindow.print(); }, 300);
  };

  const handleModalMouseDown = (e) => {
    if (e.target.closest('.rp-header')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - modalPosition.x,
        y: e.clientY - modalPosition.y,
      });
    }
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      setModalPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleTableChange = (paginationConfig, _filters, sorter) => {
    if (!serverMode) {
      setPagination(paginationConfig);
      // Client mode: data is already in memory; Ant Design slices it.
      return;
    }
    // Server mode: pagination is driven by the dedicated control below the
    // table, so this handler only reacts to column sort changes. Reset to
    // page 1 and refetch with the new sort.
    const sortFieldMap = {
      patientID: 'patient_id',
      patientName: 'patient_name',
      studyDate: 'study_date',
      referringPhysician: 'referring_physician',
      reportStatus: 'report_status',
    };
    let sort = '-study_date'; // cleared sort → back to the default (newest study date)
    if (sorter && sorter.field && sorter.order) {
      const field = sortFieldMap[sorter.field] || 'study_date';
      sort = (sorter.order === 'ascend' ? '' : '-') + field;
    }
    setSortParam(sort);
    const np = { ...pagination, current: 1 };
    setPagination(np);
    fetchStudies({ pagination: np, sort });
  };

  const rowSelection = {
    // Fixed checkbox-column width so the worklist's total column width is
    // deterministic — the page cap (--worklist-w) lands exactly on the Actions
    // edge regardless of default antd selection-column sizing.
    columnWidth: 44,
    selectedRowKeys,
    onChange: (rowKeys, rowItems) => {
      setSelectedRowKeys(rowKeys);
      setSelectedRowItems(rowItems);
    },
  };

  const resetSelectedItems = () => {
    setSelectedRowKeys([]);
    setSelectedRowItems([]);
  };

  const handleAssignSuccess = (radiologist, assignmentResult = {}) => {
    const assignedName =
      radiologist?.displayName ||
      radiologist?.username ||
      radiologist?.name ||
      radiologist ||
      'Assigned';
    const assigneeId = radiologist?.id || radiologist?.value || radiologist;
    const assignedUIDs = assignmentResult?.assignedUIDs || [];

    const selectedIds = new Set(
      (selectedRowKeys || []).length
        ? selectedRowKeys
        : selectedRowItems.map((item) => item.id)
    );

    const selectedItems = studiesData.filter(
      (item) => selectedIds.has(item.id) || selectedIds.has(item.key)
    );
    const selectedUIDSet = new Set(
      selectedItems
      .map((item) => item.studyInstanceUID)
      .filter((uid) => uid)
    );

    const persistedUIDSet = new Set(
      assignedUIDs.length > 0
        ? assignedUIDs
        : Array.from(selectedUIDSet)
    );

    if (assigneeId && persistedUIDSet.size > 0) {
      setAssignmentCache((prev) => {
        const next = { ...prev };
        persistedUIDSet.forEach((uid) => {
          next[uid] = assigneeId;
        });
        return next;
      });
    }

    setStudiesData((prev) =>
      prev.map((item) => {
        const match = selectedIds.has(item.id) || selectedIds.has(item.key);
        if (!match || !persistedUIDSet.has(item.studyInstanceUID)) return item;
        return {
          ...item,
          status: 'ASSIGNED',
          referringPhysician: assignedName,
        };
      })
    );

    resetSelectedItems();
    const count = persistedUIDSet.size;
    const nameStr = typeof assignedName === 'string' && assignedName !== 'Assigned' ? assignedName : 'radiologist';
    message.success(
      count === 1
        ? `Study assigned to ${nameStr} \u2713`
        : `${count} studies assigned to ${nameStr} \u2713`,
      3
    );
    fetchStudies();
  };

  const handleOpenViewer = async (study = null) => {
    if (processing) return;

    try {
      let listStudyUID = [];

      if (study) {
        let resolvedUID = study.studyInstanceUID;
        let studyDetail = null;
        
        // Always fetch study detail to validate series data
        // Use the actual Orthanc study ID for fetching
        if (study.id) {
          try {
            const { data } = await getStudyDetail(study.id);
            studyDetail = data;

            // Normalize the UID: strip VinLab project UUID prefix (project_id.original_uid).
            // Project studies are uploaded to Orthanc with a UUID-prefixed StudyInstanceUID.
            // QIDO-RS cannot match such non-standard UIDs (hyphens are invalid in DICOM UIDs).
            const rawUID = data?.MainDicomTags?.StudyInstanceUID || study.studyInstanceUID;
            resolvedUID = normalizeStudyUID(rawUID) || study.studyInstanceUID;
          } catch (error) {
            console.error('[VIEWER] Failed to fetch study details:', error);
            message.error('Failed to fetch study details from PACS');
            setProcessing(false);
            return;
          }
        }
        
        if (!resolvedUID) {
          console.error('[VIEWER] No StudyInstanceUID found');
          message.error('StudyInstanceUID not found for the selected study');
          setProcessing(false);
          return;
        }

        // CRITICAL: Validate that study has series data
        if (studyDetail) {
          const hasSeries = studyDetail.Series && Array.isArray(studyDetail.Series) && studyDetail.Series.length > 0;
          if (!hasSeries) {
            console.error('[VIEWER] BLOCKED - Study has no series data');
            message.error('This study has incomplete DICOM data and cannot be opened. The study needs to be re-uploaded with proper DICOM files.');
            setProcessing(false);
            return;
          }
        } else {
          console.error('[VIEWER] BLOCKED - No study detail available');
          message.error('Unable to validate study data. Please try again.');
          setProcessing(false);
          return;
        }

        listStudyUID = [
          {
            type: SESSION_TYPE.STUDY,
            id: resolvedUID,
            meta: {
              study_code: study.patientID,
              study_id: study.id,
              project_name: 'Clinical Studies',
            },
          },
        ];
      } else {
        if (selectedRowItems.length === 0 && selectedRowKeys.length === 0) {
          message.warning('Please select studies to open');
          return;
        }

        const baseItems = selectedRowKeys.length
          ? selectedRowKeys.map((key) => {
              const existing = studiesData.find(
                (item) => item.id === key || item.key === key
              );
              return existing || { id: key };
            })
          : selectedRowItems;

        const resolvedItems = await Promise.all(
          baseItems.map(async (item) => {
            // For multi-select, always fetch the study detail from Orthanc using the study ID
            if (!item.id) {
              return { ...item, resolvedUID: '', hasSeries: false };
            }
            try {
              const { data } = await getStudyDetail(item.id);
              const hasSeries = data?.Series && Array.isArray(data.Series) && data.Series.length > 0;
              const rawStudyUID = data?.MainDicomTags?.StudyInstanceUID || '';
              // Strip VinLab project UUID prefix so QIDO-RS can match the clean DICOM UID
              const studyUID = normalizeStudyUID(rawStudyUID) || rawStudyUID;
              return {
                ...item,
                resolvedUID: studyUID,
                hasSeries,
              };
            } catch (error) {
              console.error('[VIEWER] Failed to resolve study:', item.id, error);
              return { ...item, resolvedUID: '', hasSeries: false };
            }
          })
        );

        const validItems = resolvedItems.filter((item) => item.resolvedUID && item.hasSeries);
        const itemsWithoutSeries = resolvedItems.filter((item) => item.resolvedUID && !item.hasSeries);
        
        if (validItems.length === 0) {
          if (itemsWithoutSeries.length > 0) {
            message.error('Selected studies have incomplete DICOM data. Please re-upload these studies via the upload interface.');
          } else {
            message.error('StudyInstanceUID not found for the selected studies');
          }
          return;
        }

        if (validItems.length < resolvedItems.length) {
          const skippedCount = resolvedItems.length - validItems.length;
          message.warning(
            `Skipped ${skippedCount} study${skippedCount > 1 ? 'ies' : ''} with missing or incomplete data`
          );
        }

        listStudyUID = validItems.map((item) => ({
          type: SESSION_TYPE.STUDY,
          id: item.resolvedUID,
          meta: {
            study_code: item.patientID,
            study_id: item.id,
            project_name: 'Clinical Studies',
          },
        }));
      }

      setProcessing(true);
      const { data } = await actionCreateSession({ data: listStudyUID });
      const { session_id } = data?.data || {};
      if (session_id) {
        // Performed Studies always use clinical-viewer
        const viewerRoute = window.origin + '/medical-view/clinical-viewer';
        window.open(
          viewerRoute + '/' + session_id + '?time=' + Date.now(),
          '_blank'
        );
        if (!study) {
          resetSelectedItems();
        }
      } else {
        message.error('Failed to create viewer session');
      }
    } catch (error) {
      console.error('Error opening viewer:', error);
      message.error('Failed to open viewer');
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenSelection = () => {
    if (selectedRowItems.length === 1) {
      return handleOpenViewer(selectedRowItems[0]);
    }
    return handleOpenViewer();
  };

  const handleOpenV3 = async (study, mode = 'viewer') => {
    if (!study?.id) {
      message.error('Study ID not found');
      return;
    }
    try {
      const { data } = await getStudyDetail(study.id);
      const rawUID = data?.MainDicomTags?.StudyInstanceUID || study.studyInstanceUID;
      if (!rawUID) {
        message.error('StudyInstanceUID not found');
        return;
      }
      const hasSeries = data?.Series && Array.isArray(data.Series) && data.Series.length > 0;
      if (!hasSeries) {
        message.error('This study has incomplete DICOM data and cannot be opened.');
        return;
      }
      // Strip VinLab project UUID prefix — QIDO-RS needs a standard DICOM UID (no hyphens)
      const resolvedUID = normalizeStudyUID(rawUID) || study.studyInstanceUID;
      window.open('/afya-viewer/' + mode + '?StudyInstanceUIDs=' + encodeURIComponent(resolvedUID), '_blank');
    } catch (error) {
      console.error('Error opening OHIF v3 viewer:', error);
      message.error('Failed to open OHIF v3 viewer');
    }
  };

  // ── Desktop workstation (Weasis) ──────────────────────────────────────────
  // Shown to every client. It was gated per-machine on a localStorage flag
  // while the workstation was a pilot; it ships as a released product now, so
  // the action belongs in the toolbar for everyone. Clients without it
  // installed get an explanation rather than a silent no-op.
  const workstationEnabled = true;

  // Shared with the radiologist's My Worklist — see utils/workstation.js.
  // Two copies of the token hand-off would drift, and drift here means a
  // radiologist gets a stale token or an unexplained no-op.
  const handleOpenWorkstation = (study) =>
    openStudyInWorkstation(study, { getStudyDetail, actionRefreshToken });

  const handleShowAssignModal = (study = null) => {
    if (study) {
      setSelectedStudyForAction(study);
    } else if (selectedRowItems.length === 0) {
      message.warning('Please select studies to assign');
      return;
    }
    setShowAssignModal(true);
  };

  const handleShowDeleteModal = (study = null) => {
    if (study) {
      setSelectedStudyForAction(study);
    } else if (selectedRowItems.length === 0) {
      message.warning('Please select studies to delete');
      return;
    }
    setShowDeleteModal(true);
  };

  const handleUnassignStudy = (study) => {
    setSelectedStudyForAction(study);
    setShowUnassignModal(true);
  };

  const handlePerformUnassign = async (study) => {
    // Immediately remove from cache so the Assigned To column updates without waiting for a full refetch
    if (study?.studyInstanceUID) {
      setAssignmentCache((prev) => {
        const next = { ...prev };
        delete next[study.studyInstanceUID];
        return next;
      });
    }
    setShowUnassignModal(false);
    setSelectedStudyForAction(null);
    fetchStudies();
  };

  const handleShowDownloadModal = (study) => {
    setSelectedStudyForAction(study);
    setShowDownloadModal(true);
  };

  const handleShowShareModal = (study) => {
    setSelectedStudyForAction(study);
    setShowShareModal(true);
  };

  const handleRefreshData = () => {
    resetSelectedItems();
    fetchStudies();
  };

  const getComputedStatus = (study) => {
    const uid = study.studyInstanceUID;
    const reportStatus = reportStatusCache[uid];
    const isAssigned = !!assignmentCache[uid];
    // A study sent to the teleradiology network is effectively assigned (to a
    // remote radiologist), so it counts as Assigned here too.
    const isTele = !!(teleStatusMap[uid] || teleStatusMap[normalizeStudyUID(uid)]);
    // A draft means In Progress whether or not the study is formally assigned —
    // a report does not write itself, and Assigned To now names the radiologist
    // holding it ("<name> · drafting"), so this no longer reads as a
    // contradiction the way an empty owner did. Keeps the progression
    // consistent with the assigned case: New → Assigned → In Progress →
    // Completed. MUST match computeClinicalStatus in the API.
    if (reportStatus === 'submitted') return 'Completed';
    if (reportStatus === 'draft') return 'In Progress';
    if (isAssigned || isTele) return 'Assigned';
    return 'New';
  };

  // In server mode the backend already applied the report-status/status filters
  // and the physician submitted-only rule, so pass the page straight through.
  const filteredStudiesData = serverMode ? studiesData : studiesData.filter((study) => {
    // Referring physicians only ever see studies with a finalized (submitted)
    // report — never pending / in-progress work.
    if (isPhysicianUser() && reportStatusCache[study.studyInstanceUID] !== 'submitted') {
      return false;
    }
    if (searchParams.reportStatus !== 'All') {
      if (reportStatusCache[study.studyInstanceUID] !== searchParams.reportStatus) {
        return false;
      }
    }
    if (searchParams.status && searchParams.status !== 'All') {
      const computed = getComputedStatus(study);
      if (computed.toUpperCase() !== searchParams.status.toUpperCase()) {
        return false;
      }
    }
    return true;
  });

  // Column order: Checkbox (from rowSelection) | Patient ID | Patient Name | Status | Accession # | Referring Physician | Sex/Age | Body Part | Series/Images | Modality | Study Date | Assigned To | Report Status | Actions
  // Every visible column has a fixed width; a single width-less "spacer" column
  // at the very end (see below) absorbs all leftover horizontal space with
  // table-layout:fixed. That keeps the real columns tight against each other and
  // pushes the slack to the far-right edge of the table instead of blowing out
  // one column (e.g. Patient Name) and leaving a gap before the next.
  const columns = [
    {
      title: 'Patient ID',
      dataIndex: 'patientID',
      key: 'patientID',
      sorter: true,
      width: 74,
      render: (text) => <span className="patient-id-cell">{text}</span>,
    },
    {
      title: 'Patient Name',
      dataIndex: 'patientName',
      key: 'patientName',
      sorter: true,
      // Wide enough for the "N prior visits · M relevant" pill under the name.
      width: 196,
      render: (text, record) => (
        <span className="patient-name-stack">
          <span className="patient-name-cell">{text}</span>
          {renderPriorsPill(record)}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status, record) => {
        const computedStatus = getComputedStatus(record);
        const cssClass = computedStatus.toLowerCase().replace(/\s+/g, '-');
        return (
          <span className={`status-badge ${cssClass}`}>
            {computedStatus}
          </span>
        );
      },
    },
    {
      title: 'Accession #',
      dataIndex: 'accessionNumber',
      key: 'accessionNumber',
      width: 92,
      render: (text) => <span className="accession-cell">{text}</span>,
    },
    {
      // Referring / ordering physician from DICOM ReferringPhysicianName
      // (stored on each row as `orderingPhysician`). Abbreviated to keep the
      // column narrow; full name shown on hover via the title tooltip.
      title: 'Referring Physician',
      dataIndex: 'orderingPhysician',
      key: 'orderingPhysician',
      width: 130,
      render: (text) => {
        const full = text && text !== 'N/A' ? text : '';
        if (!full) return <span className="referring-unassigned">—</span>;
        return (
          <span className="ordering-physician-cell" title={full}>
            {abbreviatePhysician(full)}
          </span>
        );
      },
    },
    {
      title: 'Sex/Age',
      key: 'sexAge',
      width: 60,
      render: (text, record) => (
        <span className="sex-age-cell">{record.sex}/{record.age}</span>
      ),
    },
    {
      title: 'Body Part',
      dataIndex: 'bodyPart',
      key: 'bodyPart',
      width: 95,
    },
    {
      // No align:'center' — Studies.scss forces headers left (!important), so
      // centered cell values sat visibly off-line under the label.
      title: 'SERIES/IMAGES',
      key: 'seriesImages',
      width: 84,
      render: (_, record) => {
        const s = record.seriesCount || 0;
        const i = record.imagesCount || 0;
        if (!s && !i) return <span style={{ color: '#6B7280' }}>N/A</span>;
        return <span>{s > 0 ? `${s}s` : '—'}{i > 0 ? ` / ${i}` : ''}</span>;
      },
    },
    {
      title: 'Modality',
      dataIndex: 'modality',
      key: 'modality',
      width: 72,
      render: (text) => text ? <span className="modality-badge">{text}</span> : null,
    },
    {
      title: 'Study Date',
      dataIndex: 'studyDate',
      key: 'studyDate',
      sorter: true,
      width: 108,
      render: (text) => {
        if (!text) return null;
        const parts = text.split(' ');
        const datePart = parts[0] || text;
        const timePart = parts.slice(1).join(' ');
        return (
          <div className="study-date-cell">
            <span className="study-date-date">{datePart}</span>
            {timePart && <span className="study-date-time">{timePart}</span>}
          </div>
        );
      },
    },
    {
      title: 'Assigned To',
      dataIndex: 'referringPhysician',
      key: 'referringPhysician',
      sorter: true,
      width: 120,
      render: (text, record) => {
        if (!text || text === 'N/A') {
          // No formal assignment. If the report was signed (sign-as on a shared
          // login), attribute the study to the radiologist who actually signed it,
          // rather than showing a misleading "Unassigned" on a completed report.
          const uid = record.studyInstanceUID;
          const signer = reportSignerCache[uid];
          if (reportStatusCache[uid] === 'submitted' && signer && (signer.sub || signer.name)) {
            // Resolve the signer from Keycloak (same source as the assignee name)
            // so it reads consistently, e.g. "Dr. Biniyam Mezgebo"; fall back to
            // the name captured in the report's signer-meta.
            const kc = signer.sub ? getUserDisplayName(signer.sub) : '';
            const signerName = (kc && kc !== signer.sub) ? kc : signer.name;
            if (signerName) {
              return (
                <span className="referring-assigned">
                  {signerName}
                  <span style={{ color: '#6b7280', fontSize: '11px', marginLeft: '4px' }}>· reported</span>
                </span>
              );
            }
          }
          // Same idea for a report still being written. "In Progress" next to a
          // blank Assigned To reads as if the study were abandoned, when in fact
          // a radiologist has it open — so name whoever holds the draft.
          if (reportStatusCache[uid] === 'draft' && signer && signer.draftSub) {
            const draftName = getUserDisplayName(signer.draftSub);
            if (draftName && draftName !== signer.draftSub) {
              return (
                <span className="referring-assigned">
                  {draftName}
                  <span style={{ color: '#6b7280', fontSize: '11px', marginLeft: '4px' }}>· drafting</span>
                </span>
              );
            }
          }
          return <span className="referring-unassigned">Unassigned</span>;
        }
        // "Teleradiology" renders identically to any assignee name (plain white).
        return <span className="referring-assigned">{text}</span>;
      },
    },
    {
      title: 'Report Status',
      dataIndex: 'reportStatus',
      key: 'reportStatus',
      sorter: true,
      // Snug width that fits dot + "Completed" + the report-view icon; the icon
      // cell also gets overflow:visible (Studies.scss) so it can never clip.
      width: 122,
      render: (text, record) => renderReportStatusCell(record),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 188,
      align: 'right',
      render: (text, record) => (
        isPhysicianUser() ? (
          // Read-only physicians get ONLY the View (Eye) action — the same
          // button, styling, and viewer path (handleOpenV3 -> afya-viewer) as
          // everyone else, just without assign/download/share/delete.
          <StudyTableActions
            viewOnly
            onViewV3={(mode) => handleOpenV3(record, mode)}
            modality={record.modality}
          />
        ) : (
          <StudyTableActions
            onView={(e) => {
              e?.stopPropagation?.();
              handleOpenViewer(record);
            }}
            onViewV3={(mode) => {
              handleOpenV3(record, mode);
            }}
            onOpenWorkstation={workstationEnabled ? (e) => {
              e?.stopPropagation?.();
              handleOpenWorkstation(record);
            } : undefined}
            modality={record.modality}
            onAssign={(e) => {
              e?.stopPropagation?.();
              handleShowAssignModal(record);
            }}
            onUnassign={(e) => {
              e?.stopPropagation?.();
              handleUnassignStudy(record);
            }}
            onDownload={(e) => {
              e?.stopPropagation?.();
              handleShowDownloadModal(record);
            }}
            onShare={(e) => {
              e?.stopPropagation?.();
              handleShowShareModal(record);
            }}
            onDelete={(e) => {
              e?.stopPropagation?.();
              handleShowDeleteModal(record);
            }}
          />
        )
      ),
    },
    {
      // Invisible spacer — no title, no width. With table-layout:fixed this is
      // the single auto-width column, so it soaks up all leftover horizontal
      // space at the far-right edge and the real columns above stay tight
      // against each other (no gap after Patient Name, etc.).
      title: '',
      key: 'spacer',
      render: () => null,
    },
  ];

  // Referring physicians get a trimmed table: drop "Assigned To" (they don't
  // need the radiologist-assignee) and make Actions compact (just the View eye).
  const displayColumns = isPhysicianUser()
    ? columns
        .filter((c) => c.key !== 'referringPhysician')
        .map((c) => (c.key === 'actions' ? { ...c, width: 90, align: 'center' } : c))
    : columns;

  const handleSearch = () => {
    const newParams = {
      ...searchParams,
      patientName: searchQuery,
      patientID: searchQuery,
      accessionNumber: searchQuery,
    };
    setSearchParams(newParams);
    const np = { ...pagination, current: 1 };
    setPagination(np);
    if (serverMode) fetchStudies({ searchParams: newParams, pagination: np, search: searchQuery });
    else fetchStudies();
  };

  const handleClear = () => {
    setSearchQuery('');
    const cleared = {
      patientName: '',
      patientID: '',
      accessionNumber: '',
      modality: 'All',
      status: 'All',
      reportStatus: 'All',
      dateRange: null,
    };
    setSearchParams(cleared);
    const np = { ...pagination, current: 1 };
    setPagination(np);
    if (serverMode) fetchStudies({ searchParams: cleared, pagination: np, search: '' });
    else fetchStudies();
  };

  // applyFilter updates a single advanced-filter field. In client mode it just
  // updates state (unchanged behavior); in server mode it also resets to page 1
  // and refetches with the new filter applied server-side.
  const applyFilter = (patch) => {
    const newParams = { ...searchParams, ...patch };
    setSearchParams(newParams);
    if (serverMode) {
      const np = { ...pagination, current: 1 };
      setPagination(np);
      fetchStudies({ searchParams: newParams, pagination: np, search: searchQuery });
    }
  };

  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
  };

  const handleUploadSuccess = () => {
    message.success('Study uploaded successfully ✓', 3);
    // fetchStudies is called by handleRefreshData which is always invoked alongside this
  };

  return (
    <div className="common-style-page studies-page">
      <div className="top-content">

        {/* Search Bar Row */}
        <div className="advanced-search">
          <div className="search-row">
            <div className="search-input-wrapper">
              <Search className="search-input-icon" />
              <input
                className="search-input"
                placeholder="Search by Patient Name, Patient ID, or Accession Number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              />
            </div>
            <button
              className={`filters-toggle-btn${showAdvancedFilters ? ' active' : ''}`}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              <Filter className="filters-toggle-icon" />
              Filters
              <ChevronDown className={`filters-chevron${showAdvancedFilters ? ' rotated' : ''}`} />
            </button>
            <button className="search-btn" onClick={handleSearch}>
              <Search className="search-btn-icon" />
              Search
            </button>
            {/* Upload moved here from the ContentHeader so the top bar keeps
                only the facility badge. Drives this page's own UploadStudyDialog. */}
            {!isPhysicianUser() && (
              <button className="upload-study-btn" onClick={() => setShowUploadModal(true)}>
                <UploadIcon className="upload-study-btn-icon" />
                Upload Study
              </button>
            )}
          </div>

          {/* Collapsible Advanced Filter Panel */}
          {showAdvancedFilters && (
            <div className="advanced-filter-panel">
              <div className="filter-grid">
                <div className="filter-field">
                  <label className="filter-field-label">
                    <Calendar className="filter-field-label-icon" />
                    Date From
                  </label>
                  <DatePicker
                    placeholder="Select date"
                    value={searchParams.dateRange ? searchParams.dateRange[0] : null}
                    onChange={(date) => {
                      const current = searchParams.dateRange || [null, null];
                      applyFilter({ dateRange: [date, current[1]] });
                    }}
                    className="filter-date-input"
                  />
                </div>
                <div className="filter-field">
                  <label className="filter-field-label">
                    <Calendar className="filter-field-label-icon" />
                    Date To
                  </label>
                  <DatePicker
                    placeholder="Select date"
                    value={searchParams.dateRange ? searchParams.dateRange[1] : null}
                    onChange={(date) => {
                      const current = searchParams.dateRange || [null, null];
                      applyFilter({ dateRange: [current[0], date] });
                    }}
                    className="filter-date-input"
                  />
                </div>
                <div className="filter-field">
                  <label className="filter-field-label">
                    <Activity className="filter-field-label-icon" />
                    Modality
                  </label>
                  <Select
                    value={searchParams.modality}
                    onChange={(value) => applyFilter({ modality: value })}
                    className="filter-select"
                    dropdownClassName="filter-dropdown"
                  >
                    <Option value="All">All Modalities</Option>
                    <Option value="CR">CR (X-ray)</Option>
                    <Option value="CT">CT</Option>
                    <Option value="DX">DX (X-ray)</Option>
                    <Option value="MG">MG (Mammography)</Option>
                    <Option value="MR">MR</Option>
                    <Option value="NM">NM (Nuclear Med)</Option>
                    <Option value="PT">PT (PET)</Option>
                    <Option value="RF">RF (Fluoroscopy)</Option>
                    <Option value="US">US (Ultrasound)</Option>
                    <Option value="XA">XA (Angiography)</Option>
                  </Select>
                </div>
                <div className="filter-field">
                  <label className="filter-field-label">
                    <Activity className="filter-field-label-icon" />
                    Status
                  </label>
                  <Select
                    value={searchParams.status}
                    onChange={(value) => applyFilter({ status: value })}
                    className="filter-select"
                    dropdownClassName="filter-dropdown"
                  >
                    <Option value="All">All Status</Option>
                    <Option value="New">New</Option>
                    <Option value="Assigned">Assigned</Option>
                    <Option value="In Progress">In Progress</Option>
                    <Option value="Completed">Completed</Option>
                  </Select>
                </div>
                <div className="filter-field">
                  <label className="filter-field-label">
                    <FileText className="filter-field-label-icon" />
                    Report Status
                  </label>
                  <Select
                    value={searchParams.reportStatus}
                    onChange={(value) => applyFilter({ reportStatus: value })}
                    className="filter-select"
                    dropdownClassName="filter-dropdown"
                  >
                    <Option value="All">All Reports</Option>
                    <Option value="none">No Report</Option>
                    <Option value="draft">Draft</Option>
                    <Option value="submitted">Submitted</Option>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Studies Table */}
      <div className="table-wrapper">
        {/* Bulk Selection Bar — Figma: appears above table rows when items selected */}
        {selectedRowKeys.length > 1 && (
          <div className="bulk-selection-bar">
            <div className="bulk-bar-left">
              <span className="bulk-bar-count">
                {selectedRowKeys.length} {selectedRowKeys.length === 1 ? 'study' : 'studies'} selected
              </span>
              <Button
                size="small"
                icon={<Download size={14} />}
                onClick={() => setShowBulkDownloadModal(true)}
                className="bulk-bar-btn"
              >
                Download All
              </Button>
              <Button
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => setShowBulkAssignModal(true)}
                className="bulk-bar-btn"
              >
                Bulk Assign
              </Button>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => setShowBulkDeleteModal(true)}
                className="bulk-bar-btn bulk-bar-btn-danger"
              >
                Delete Selected
              </Button>
            </div>
            <Button
              type="text"
              size="small"
              onClick={resetSelectedItems}
              className="bulk-bar-clear"
            >
              Clear Selection
            </Button>
          </div>
        )}
        <Table
          key={tableRenderKey}
          className="dark-table"
          columns={displayColumns}
          dataSource={filteredStudiesData}
          rowKey="id"
          loading={loading}
          rowSelection={isPhysicianUser() ? undefined : rowSelection}
          // Suppress the "No data" empty illustration while a fetch is in
          // flight. Ant Design renders the empty state and the loading
          // spinner concurrently when dataSource is [], so on first paint
          // (and on every refresh before data arrives) the user otherwise
          // sees "No data" flickering through the spinner overlay.
          locale={loading ? { emptyText: <span /> } : undefined}
          pagination={serverMode ? false : {
            ...pagination,
            total: filteredStudiesData.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showTotal: (total, range) => (
              <span>
                Showing <span style={{ fontWeight: 600, color: '#fff' }}>{range[0]}</span> to{' '}
                <span style={{ fontWeight: 600, color: '#fff' }}>{range[1]}</span> of{' '}
                <span style={{ fontWeight: 600, color: '#fff' }}>{total}</span> studies
              </span>
            ),
          }}
          onChange={handleTableChange}
          scroll={{ x: 1474, y: 'calc(100vh - 330px)' }}
          onRow={(record) => ({
            onDoubleClick: (event) => {
              event.stopPropagation();
              event.preventDefault();
              // Don't trigger if clicking on checkbox
              if (event?._targetInst?.elementType === 'input') {
                return;
              }
              handleOpenV3(record, 'viewer');
            },
            style: { cursor: 'pointer' },
          })}
        />
        {/* Server-side pagination: a dedicated control drives page/size changes
            (Ant Table client-slices dataSource, so in server mode we disable its
            built-in pagination and feed it only the current page). It reuses the
            table's dark-theme pagination styling via the .dark-table wrapper +
            ant-table-pagination class, so it matches the original look. */}
        {serverMode && (
          <div className="dark-table">
            <Pagination
              className="ant-table-pagination"
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={serverTotal}
              showSizeChanger
              pageSizeOptions={['10', '25', '50', '100']}
              showTotal={(total, range) => (
                <span>
                  Showing <span style={{ fontWeight: 600, color: '#fff' }}>{range[0]}</span> to{' '}
                  <span style={{ fontWeight: 600, color: '#fff' }}>{range[1]}</span> of{' '}
                  <span style={{ fontWeight: 600, color: '#fff' }}>{total}</span> studies
                </span>
              )}
              onChange={(page, pageSize) => {
                const np = { current: page, pageSize };
                setPagination(np);
                fetchStudies({ pagination: np, sort: sortParam });
              }}
            />
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <UploadStudyDialog
        open={showUploadModal}
        onClose={handleCloseUploadModal}
        onSuccess={() => {
          console.log('[Studies] Upload onSuccess fired — incrementing refreshKey to trigger useEffect');
          handleUploadSuccess();
          // Increment refreshKey so the useEffect re-runs with fresh closures.
          // This is immune to stale-closure bugs because the useEffect fires
          // AFTER React re-renders, giving fetchStudies the latest state.
          setRefreshKey(prev => prev + 1);
        }}
      />

      {/* Assign Modal */}
      <AssignStudyDialog
        study={selectedStudyForAction || (selectedRowItems.length > 0 ? selectedRowItems[0] : null)}
        open={showAssignModal}
        onOpenChange={(open) => {
          setShowAssignModal(open);
          if (!open) setSelectedStudyForAction(null);
        }}
        onSuccess={handleAssignSuccess}
        reportStatus={reportStatusCache[selectedStudyForAction?.studyInstanceUID] || 'none'}
        assigneeId={assignmentCache[selectedStudyForAction?.studyInstanceUID] || null}
      />

      {/* Delete Modal */}
      <DeleteStudyDialog
        study={selectedStudyForAction || (selectedRowItems.length > 0 ? selectedRowItems[0] : null)}
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedStudyForAction(null);
        }}
        onSuccess={handleRefreshData}
      />

      {/* Unassign Modal */}
      <UnassignStudyDialog
        study={selectedStudyForAction}
        open={showUnassignModal}
        onOpenChange={(open) => {
          setShowUnassignModal(open);
          if (!open) setSelectedStudyForAction(null);
        }}
        onUnassign={handlePerformUnassign}
        reportStatus={reportStatusCache[selectedStudyForAction?.studyInstanceUID] || 'none'}
        assigneeId={assignmentCache[selectedStudyForAction?.studyInstanceUID] || null}
      />

      {/* Download Modal */}
      <DownloadStudyDialog
        study={selectedStudyForAction}
        open={showDownloadModal}
        onOpenChange={(open) => {
          setShowDownloadModal(open);
          if (!open) setSelectedStudyForAction(null);
        }}
      />

      {/* Share Modal */}
      <ShareStudyDialog
        study={selectedStudyForAction}
        open={showShareModal}
        onOpenChange={(open) => {
          setShowShareModal(open);
          if (!open) setSelectedStudyForAction(null);
        }}
        onSuccess={handleRefreshData}
      />

      {/* Bulk Download Modal */}
      <BulkDownloadDialog
        studies={selectedRowItems}
        open={showBulkDownloadModal}
        onOpenChange={(open) => {
          setShowBulkDownloadModal(open);
        }}
      />

      {/* Bulk Assign Modal */}
      <BulkAssignDialog
        studyIds={selectedRowKeys}
        studies={selectedRowItems}
        open={showBulkAssignModal}
        onOpenChange={(open) => {
          setShowBulkAssignModal(open);
        }}
        onAssign={() => {
          setShowBulkAssignModal(false);
          handleRefreshData();
        }}
        users={users}
      />

      {/* Bulk Delete Modal */}
      <BulkDeleteDialog
        studies={selectedRowItems}
        open={showBulkDeleteModal}
        onOpenChange={(open) => {
          setShowBulkDeleteModal(open);
        }}
        onDelete={() => {
          setShowBulkDeleteModal(false);
          handleRefreshData();
        }}
      />

      {/* Report Preview Modal */}
      {historyPanelRow && historyPanel && historyPanel.visits.length > 0 && (
        <PatientHistoryPanel
          row={historyPanelRow}
          visits={historyPanel.visits}
          relevant={historyPanel.relevant}
          impressions={priorImpressions}
          onClose={closeHistoryPanel}
          onOpenReport={handleOpenPriorReport}
          onOpenViewer={(row) => { setHistoryPanelRow(null); handleOpenV3(row, 'viewer'); }}
        />
      )}
      {showReportPreviewModal && (
        <div className="rp-overlay" onClick={() => setShowReportPreviewModal(false)}>
          <div
            className="rp-dialog"
            style={{ transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)` }}
            onMouseDown={handleModalMouseDown}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rp-header">
              <span className="rp-header-title">Report Preview</span>
              <button type="button" className="rp-close" onClick={() => setShowReportPreviewModal(false)}>&#10005;</button>
            </div>
            <div className="rp-body">
              {reportPreviewLoading ? (
                <div className="rp-loading">
                  <Spin size="large" />
                  <p>Loading report...</p>
                </div>
              ) : reportPreviewData && reportPreviewData.content ? (
                <>
                  <h1>DIAGNOSTIC REPORT</h1>
                  <table>
                    <tbody>
                      <tr>
                        <td><strong>Patient</strong></td>
                        <td>{reportPreviewMeta?.patientName || reportPreviewStudy?.patientName || 'N/A'}</td>
                        <td><strong>Referring Physician</strong></td>
                        <td>{reportPreviewMeta?.referringPhysician || reportPreviewStudy?.orderingPhysician || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><strong>Patient ID</strong></td>
                        <td>{reportPreviewMeta?.patientId || reportPreviewStudy?.patientID || 'N/A'}</td>
                        <td><strong>Date</strong></td>
                        <td>{reportPreviewMeta?.studyDate || reportPreviewStudy?.studyDate || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><strong>Sex / Age</strong></td>
                        <td>{(() => {
                          const pm = reportPreviewMeta;
                          if (pm && (pm.patientSex || pm.patientAge)) {
                            return (pm.patientSex || '') + (pm.patientAge ? ` / ${pm.patientAge}` : '');
                          }
                          return `${reportPreviewStudy?.sex || 'N/A'}${reportPreviewStudy?.age ? ` / ${reportPreviewStudy.age}` : ''}`;
                        })()}</td>
                        <td><strong>Modality</strong></td>
                        <td>{reportPreviewMeta?.modality || reportPreviewStudy?.modality || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div dangerouslySetInnerHTML={{ __html: reportPreviewData.content.replace(/^\s*<h1[^>]*>.*?DIAGNOSTIC\s+REPORT.*?<\/h1>\s*/is, '').replace(/^\s*<table[\s\S]*?<\/table>\s*/i, '') }} />
                  {reportPreviewData.status === 'submitted' && (() => {
                    const name = reportPreviewRadiologistName;
                    const displayName = name
                      ? (/^dr\.?\s/i.test(name) ? name : 'Dr. ' + name)
                      : '';
                    const submittedDate = reportPreviewData.submitted_at
                      ? new Date(reportPreviewData.submitted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
                        ' ' + new Date(reportPreviewData.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                      : '';
                    if (!displayName && !submittedDate) return null;
                    return (
                      <div style={{ marginTop: '48px', paddingTop: '16px', borderTop: '1px solid #d1d5db' }}>
                        <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Electronically Signed By</div>
                        {displayName && <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>{displayName}</div>}
                        {submittedDate && <div style={{ fontSize: '11px', color: '#6b7280' }}>{submittedDate}</div>}
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="rp-loading">
                  <p>No report data available.</p>
                </div>
              )}
            </div>
            <div className="rp-footer">
              <button type="button" className="rp-footer-pdf" onClick={handleReportPreviewDownloadPDF} disabled={reportPreviewLoading || !reportPreviewData}>
                <Download size={14} /> Download PDF
              </button>
              <button type="button" className="rp-footer-close" onClick={() => setShowReportPreviewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  uploadInfoModal: state.system.uploadInfoModal,
  users: state.system.users,
  profile: state.system.profile,
  currentProject: state.studyList?.currentProject || {},
});

const mapDispatchToProps = {
  actionShowUploadModal,
  actionGetUsers,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Studies));
